import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { AuthPageLayoutComponent } from '../../../shared/layout/auth-page-layout/auth-page-layout.component';
import { LabelComponent } from '../../../shared/components/form/label/label.component';
import { InputFieldComponent } from '../../../shared/components/form/input/input-field.component';
import { AuthApiService } from '../services/auth-api.service';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';

/**
 * Pantalla dual de recuperación de contraseña (INC 14).
 *
 * - Sin `?token=` → modo "solicitar": pide el correo y llama forgot-password.
 *   El backend ya NO responde 200 neutro: un correo no registrado devuelve 404,
 *   que se muestra como error DEBAJO del campo (nunca como éxito verde). El 429
 *   (rate limit) se muestra como toast.
 * - Con `?token=<t>` → modo "restablecer": pide la nueva contraseña (min 8) y la
 *   confirmación, llama reset-password y redirige a /signin al terminar. El reset
 *   revoca TODOS los refresh tokens del usuario, así que se limpia la sesión local
 *   para forzar un re-login limpio.
 */
@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    AuthPageLayoutComponent,
    LabelComponent,
    InputFieldComponent,
  ],
  templateUrl: './reset-password.component.html',
})
export class ResetPasswordComponent implements OnInit {
  mode: 'request' | 'reset' = 'request';
  token = '';

  // Modo solicitar
  email = '';
  requestSent = false;
  requestMessage = '';
  /** Error de campo bajo el input de correo (formato inválido o 404 "no registrado"). */
  emailError = '';

  /** Formato de correo validado en cliente ANTES de llamar al backend. */
  private readonly EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  // Modo restablecer
  newPassword = '';
  confirmPassword = '';
  showPassword = false;
  resetDone = false;

  submitting = false;
  error = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authApi: AuthApiService,
    private authService: AuthService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    // Suscripción (no snapshot): el enlace del correo puede llegar con el token en
    // el query string sin recrear el componente (mismo patrón que sign-in).
    this.route.queryParamMap.subscribe((params) => {
      const token = params.get('token');
      if (token) {
        this.token = token;
        this.mode = 'reset';
      } else {
        this.token = '';
        this.mode = 'request';
      }
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  onFieldChange(): void {
    if (this.error) this.error = '';
    if (this.emailError) this.emailError = '';
  }

  // ── Modo solicitar ──────────────────────────────────────────────
  onRequest(): void {
    const mail = this.email.trim();
    this.emailError = '';
    this.error = '';

    if (!mail) {
      this.emailError = 'Ingresa tu correo electrónico.';
      return;
    }
    if (!this.EMAIL_PATTERN.test(mail)) {
      this.emailError = 'Ingresa un correo electrónico válido.';
      return;
    }

    this.submitting = true;

    this.authApi.forgotPassword(mail).subscribe({
      next: (res) => {
        this.submitting = false;
        this.requestSent = true;
        this.requestMessage =
          res?.message?.trim() ||
          'Te enviamos un correo con las instrucciones para restablecer tu contraseña.';
      },
      error: (err) => {
        this.submitting = false;
        const backendMsg = err?.error?.message?.trim();

        // 404: el correo no está registrado → error de CAMPO, nunca éxito verde.
        if (err?.status === 404) {
          this.emailError = backendMsg || 'Ese correo no está registrado en el sistema.';
          return;
        }

        // 429: rate limit (por correo o por IP) → toast.
        if (err?.status === 429) {
          this.toast.warning({
            title: 'Demasiados intentos',
            message: backendMsg || 'Demasiados intentos. Espera un minuto e inténtalo de nuevo.',
          });
          return;
        }

        this.error = backendMsg || 'No se pudo procesar la solicitud. Intenta nuevamente.';
      },
    });
  }

  // ── Modo restablecer ────────────────────────────────────────────
  onReset(): void {
    if (!this.newPassword || !this.confirmPassword) {
      this.error = 'Completa ambos campos de contraseña.';
      return;
    }
    if (this.newPassword.length < 8) {
      this.error = 'La contraseña debe tener al menos 8 caracteres.';
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.error = 'Las contraseñas no coinciden.';
      return;
    }

    this.submitting = true;
    this.error = '';

    this.authApi.resetPassword(this.token, this.newPassword).subscribe({
      next: (res) => {
        this.submitting = false;
        this.resetDone = true;
        // El backend revocó TODOS los refresh tokens del usuario: cualquier sesión
        // guardada en este navegador ya no sirve → limpiar y forzar re-login.
        this.authService.logout();
        this.toast.success({
          title: 'Contraseña actualizada',
          message: res?.message?.trim() || 'Tu contraseña fue actualizada. Ya puedes iniciar sesión.',
        });
        setTimeout(() => this.router.navigate(['/signin']), 2000);
      },
      error: (err) => {
        this.submitting = false;
        this.error =
          err?.error?.message?.trim() ||
          'El enlace de restablecimiento no es válido, ya fue usado o expiró. Solicita uno nuevo.';
      },
    });
  }
}
